import {useEffect, useRef, useState} from 'react';
import {AppState, Platform} from 'react-native';
import {NovaVoiceModule} from '@shared/nativeModules/NovaVoiceModule';
import type * as WakeWordEngine from './wakeWordEngine';
import type {WakeWordEar} from './wakeWordEngine';
import type {WakeWordEngineName} from './wakeWordConfig';
import {describeError, noteMic} from './wakeWordDiagnostics';

type Engine = Pick<typeof WakeWordEngine, 'listenForWakeWord'>;

/**
 * The engine — and ONNX Runtime with it — comes in its own chunk on the web,
 * fetched the first time someone switches Hey Nova on, and most technicians
 * never pay for it. On the phone it is wakeWordEngine.native.ts. Picovoice's —
 * porcupineEngine.web.ts, or porcupineEngine.ios.ts on iOS — loads only once a
 * tester picks it: on the web it is a chunk of its own too.
 */
const loadEngine = (engine: WakeWordEngineName): Promise<Engine> =>
  engine === 'picovoice'
    ? import('./porcupineEngine')
    : import('./wakeWordEngine');

/**
 * - off: the switch is off, or the environment does not offer Hey Nova.
 * - starting: loading the models or opening the microphone — on a phone also
 *   reopening it, and its first second, before "Hey Nova" can be heard whole.
 * - listening: the microphone is open and the models are scoring it.
 * - waiting: open, but the browser holds the audio until the technician taps
 *   or types on the page — after a reload with Hey Nova already on, say.
 * - paused: switched on, but stepped aside — Nova's own mic is open, a turn is
 *   running, Nova is talking, or the tab is hidden.
 * - blocked: the browser refused the microphone.
 * - unavailable: anything else went wrong; the console says what.
 */
export type WakeWordState =
  | 'off'
  | 'starting'
  | 'listening'
  | 'waiting'
  | 'paused'
  | 'blocked'
  | 'unavailable';

interface Options {
  /** The environment offers Hey Nova and the technician switched it on. */
  enabled: boolean;
  /** Step aside for now: the chat's own microphone or Nova's voice is in use. */
  paused: boolean;
  onWake: () => void;
  /** Which engine listens; switching it closes one and opens the other. */
  engine?: WakeWordEngineName;
  /** Where the engine comes from. Tests pass their own: Jest cannot run import(). */
  load?: (engine: WakeWordEngineName) => Promise<Engine>;
}

/**
 * Whether this browser — or this build of the app — can listen for Hey Nova
 * at all. Here rather than in the engine, so asking does not load ONNX
 * Runtime.
 */
export const isWakeWordSupported = (): boolean =>
  Platform.OS === 'web'
    ? typeof window !== 'undefined' &&
      typeof WebAssembly !== 'undefined' &&
      typeof AudioWorkletNode !== 'undefined' &&
      Boolean(navigator.mediaDevices?.getUserMedia)
    : NovaVoiceModule.hasWakeMic();

/** The microphone was refused — a DOMException, which not every browser makes an Error. */
const refused = (error: unknown): boolean => {
  const name = (error as {name?: unknown} | null)?.name;
  return name === 'NotAllowedError' || name === 'SecurityError';
};

/**
 * Listens for "Hey Nova" while enabled. The microphone opens once and stays
 * open while the switch is on: pausing — the chat's own mic, an answer, Nova
 * talking — only stops the scoring, and resuming starts from fresh buffers so
 * a wake is never scored across the gap. Reopening it around every question
 * left it deaf after the first answer, in a browser that starts each new
 * audio context suspended until a tap.
 *
 * A hidden tab, or the app in the background, does close it: nobody is
 * looking at Nova then, and an open microphone there is the technician's to
 * grant, not the app's to keep.
 */
export const useWakeWord = ({
  enabled,
  paused,
  onWake,
  engine = 'openwakeword',
  load = loadEngine,
}: Options) => {
  // A hidden tab, or the app in the background (AppState is the tab's
  // visibility on the web).
  const [hidden, setHidden] = useState(
    () => AppState.currentState === 'background',
  );
  const [ear, setEar] = useState<WakeWordEar | null>(null);
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<'blocked' | 'unavailable' | null>(
    null,
  );
  const onWakeRef = useRef(onWake);
  onWakeRef.current = onWake;
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const subscription = AppState.addEventListener('change', next =>
      setHidden(next === 'background'),
    );
    return () => subscription.remove();
  }, []);

  // Open the microphone while switched on and visible; close it otherwise.
  useEffect(() => {
    if (!enabled || hidden) {
      return;
    }
    let cancelled = false;
    let opened: WakeWordEar | null = null;
    setFailure(null);
    loadRef
      .current(engine)
      .then(loaded =>
        loaded.listenForWakeWord(
          () => onWakeRef.current(),
          problem => {
            console.warn('[NovaAI] Hey Nova stopped listening:', problem);
            // Close the microphone: open and not listening is the worst of both.
            opened?.stop();
            noteMic('failed', describeError(problem));
            if (!cancelled) {
              setEar(null);
              setFailure('unavailable');
            }
          },
          isRunning => {
            if (!cancelled) {
              setRunning(isRunning);
            }
          },
        ),
      )
      .then(started => {
        if (cancelled) {
          started.stop();
          return;
        }
        opened = started;
        setEar(started);
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        console.warn('[NovaAI] Hey Nova could not start:', error);
        noteMic('failed', describeError(error));
        setFailure(refused(error) ? 'blocked' : 'unavailable');
      });
    return () => {
      cancelled = true;
      opened?.stop();
      setEar(null);
      setRunning(false);
    };
  }, [enabled, hidden, engine]);

  // Step aside and back without touching the microphone.
  useEffect(() => {
    if (!ear) {
      return;
    }
    if (paused) {
      ear.pause();
    } else {
      ear.resume();
    }
  }, [ear, paused]);

  const state: WakeWordState = !enabled
    ? 'off'
    : failure
    ? failure
    : hidden || (ear && paused)
    ? 'paused'
    : !ear
    ? 'starting'
    : running
    ? 'listening'
    : Platform.OS === 'web'
    ? 'waiting'
    : 'starting';

  return {state};
};
